import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Form, ProgressBar } from "react-bootstrap";
import {
    FaFileAlt,
    FaClock,
    FaCheckCircle,
    FaTimesCircle,
    FaStopwatch,
    FaEye,
    FaDownload,
} from "react-icons/fa";
import "./DashboardStats.css";
import { AgGridReact } from "ag-grid-react";
import {
    ArrowUpLong,
    CircleInfo,
    Cross,
    Download,
    EyeIcon,
    FileCsv,
    FileExcel,
    FilePdf,
    Filter,
    Search,
} from "../../../../../../../components/icons/Icons";
import Widgets from "../../../../../../../components/Widgets";
import TableSkeleton from "../../../../../../../components/table/TableSkeleton.js";
import CustomPagination from "../../../../../../../components/CustomPagination";
import BarChart from "../../../../../../../components/Charts/BarChart.js";
import DeductionBreakDown from "../../../../../../../components/Charts/DeductionBreakDown.js";
import {
    getCapitalizeWord,
    getInitials,
    getInitialColor,
    currentFynencialYear,
    useSelect,
} from "../../../../../../../services/commonServices";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    BranchList,
    DepartmentList,
    RegimetList,
    FinancialYear,
} from "../../../../../../../components/FormElements/index.js";
import {
    getTdsAllData,
    getTdsEmpListData,
    initAllTds,
    initTdsEmpList,
} from "../../../../../../../redux/slices/tdsSlice.js";
import {
    getEmployeePayrollRecordsPath,
    getTDSDashboardPath,
} from "../../../../../../../services/config.js";
import { toast } from "react-toastify";

const Dashboard = () => {
    useEffect(() => {
        localStorage.removeItem("tdsUser");
    }, []);

    const user = JSON.parse(localStorage.getItem("user_data")) || {};
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const tdsStore = useSelect("tds");

    const [tdsAllRecord, setTdsAllRecord] = useState({
        totalTDSSubmitted: 0,
        totalTDSTrend: "+0% vs last month",
        pendingVerifications: 0,
        approved: 0,
        approvedRate: "0%",
        rejected: 0,
        rejectedRate: "0%",
        avgProcessingTime: "0",
        panPresentPercentage: 0,
        panPresentCount: 0,
        panMissingPercentage: 0,
        panMissingCount: 0,
        deductionBreakdown: [],
        processingTrends: [],
        financialYear: currentFynencialYear(),
        totalEmployees: 0,
    });

    const [tdsEmpList, setTdsEmpList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [empListLoading, setEmpListLoading] = useState(true);

    const [state, setState] = useState({
        financialYear: currentFynencialYear(),
        departmentId: "",
        regimeType: "old",
        branchId: "",
    });

    const [regimeType, setRegimeType] = useState("old");
    const [gridApi, setGridApi] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [isExporting, setIsExporting] = useState(false);

    const paginationPageSize = 10;
    const paginationPageSizeSelector = [10, 20, 50, 100];

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const navigateToTaxDeclarations = (staffId) => {
        const currentPath = window.location.pathname + window.location.search;
        const dashboardState = {
            filters: state,
            searchText,
            sortBy,
            path: currentPath,
            timestamp: Date.now(),
        };
        localStorage.setItem("tdsDashboardState", JSON.stringify(dashboardState));
        navigate(`/empmanagement/user-info/${staffId}#tax-declarations`);
    };

    const exportToCSV = () => {
        if (!tdsEmpList.length) {
            toast.error("No data to export");
            return;
        }
        setIsExporting(true);
        try {
            const csvData = tdsEmpList.map((emp) => ({
                "Employee Name": emp.employeeName || "N/A",
                "Employee ID": emp.employeeId || "N/A",
                "Department": emp.departmentName || "N/A",
                "Status": emp.tdsStatus || "pending",
                "Regime Type": emp.regimeType || "N/A",
                "TDS Amount": emp.tdsAmount || "0",
                "Deductions": emp.deductions || "0",
                "Compliance": emp.tdsComplaintStatus || "non-compliant",
            }));
            const headers = Object.keys(csvData[0]).join(",");
            const rows = csvData.map((row) =>
                Object.values(row)
                    .map((value) => `"${String(value).replace(/"/g, '""')}"`)
                    .join(",")
            );
            const csvContent = [headers, ...rows].join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `TDS_Employee_Report_${state.financialYear}_${new Date().toISOString().slice(0, 10)}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success("CSV exported successfully!");
        } catch (error) {
            toast.error("Failed to export CSV");
        } finally {
            setIsExporting(false);
        }
    };

    const exportToExcel = () => {
        toast.info("Excel export requires xlsx package. Exporting as CSV instead.");
        exportToCSV();
    };

    const exportToPDF = () => {
        if (!tdsEmpList.length) {
            toast.error("No data to export");
            return;
        }
        setIsExporting(true);
        try {
            const printWindow = window.open("", "_blank");
            if (!printWindow) {
                toast.error("Pop-up blocked. Please allow pop-ups.");
                setIsExporting(false);
                return;
            }
            printWindow.document.write(`
                <html>
                    <head><title>TDS Report - ${state.financialYear}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #9b51e0; color: white; }
                    </style>
                    </head>
                    <body>
                        <h1>TDS Employee Report - ${state.financialYear}</h1>
                        <table>
                            <thead><tr>
                                <th>Name</th><th>ID</th><th>Department</th><th>Status</th>
                                <th>Regime</th><th>TDS Amount</th><th>Deductions</th><th>Compliance</th>
                            </tr></thead>
                            <tbody>
                                ${tdsEmpList.map(emp => `
                                    <tr>
                                        <td>${emp.employeeName || "N/A"}</td>
                                        <td>${emp.employeeId || "N/A"}</td>
                                        <td>${emp.departmentName || "N/A"}</td>
                                        <td>${emp.tdsStatus || "pending"}</td>
                                        <td>${emp.regimeType || "N/A"}</td>
                                        <td>${emp.tdsAmount || "0"}</td>
                                        <td>${emp.deductions || "0"}</td>
                                        <td>${emp.tdsComplaintStatus || "non-compliant"}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                        <script>window.onload = () => window.print();</script>
                    </body>
                </html>
            `);
            printWindow.document.close();
            toast.success("PDF print dialog opened!");
        } catch (error) {
            toast.error("Failed to export PDF");
        } finally {
            setIsExporting(false);
        }
    };

    const exportBulkData = () => {
        exportToCSV();
        toast.success("Bulk export completed (CSV)");
    };

    const handleExport = (format) => {
        if (isExporting || tdsEmpList.length === 0) {
            toast.info("Export in progress or no data");
            return;
        }
        switch (format) {
            case "PDF": exportToPDF(); break;
            case "XLSX": exportToExcel(); break;
            case "CSV": exportToCSV(); break;
            case "Bulk": exportBulkData(); break;
            default: toast.error("Invalid format");
        }
    };

    const downloadEmployeeReport = (employeeData) => {
        if (!employeeData) {
            toast.error("No employee data available");
            return;
        }
        try {
            const csvData = [{
                "Employee Name": employeeData.employeeName || "N/A",
                "Employee ID": employeeData.employeeId || "N/A",
                "Department": employeeData.departmentName || "N/A",
                "Status": employeeData.tdsStatus || "pending",
                "Regime Type": employeeData.regimeType || "N/A",
                "TDS Amount": employeeData.tdsAmount || "0",
                "Deductions": employeeData.deductions || "0",
                "Compliance": employeeData.tdsComplaintStatus || "non-compliant"
            }];
            const headers = Object.keys(csvData[0]).join(",");
            const rows = csvData.map(row =>
                Object.values(row).map(value => `"${String(value).replace(/"/g, '""')}"`).join(",")
            );
            const csvContent = [headers, ...rows].join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `TDS_Report_${employeeData.employeeName || employeeData.employeeId}_${state.financialYear}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`Report downloaded for ${employeeData.employeeName || "employee"}`);
        } catch (error) {
            toast.error("Failed to download report");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({ ...prev, [name]: value }));
    };

    const setSelectedYear = (value) => {
        setState((prev) => ({ ...prev, financialYear: value }));
    };

    const clearAllFilter = () => {
        setState({
            financialYear: currentFynencialYear(),
            departmentId: "",
            regimeType: "old",
            branchId: "",
        });
        setRegimeType("old");
        setSearchText("");
        setSortBy("name");
        localStorage.removeItem("tdsDashboardState");
        toast.success("All filters cleared");
    };

    const applyFilters = () => {
        getAllTdsData();
        getTdsEmpPayrollData();
        toast.success("Filters applied successfully!");
    };

    const getAllTdsData = useCallback(() => {
        setLoading(true);
        dispatch(
            getTdsAllData({
                path: getTDSDashboardPath,
                data: {
                    userId: user.user_id,
                    financialYear: state.financialYear,
                    departmentId: state.departmentId || null,
                    regimeType: state.regimeType || null,
                    branchId: state.branchId || null,
                },
            })
        );
    }, [dispatch, user.user_id, state]);

    const getTdsEmpPayrollData = useCallback(() => {
        setEmpListLoading(true);
        dispatch(
            getTdsEmpListData({
                path: getEmployeePayrollRecordsPath,
                data: {
                    userId: user.user_id,
                    financialYear: state.financialYear,
                    departmentId: state.departmentId || null,
                    regimeType: state.regimeType || null,
                    branchId: state.branchId || null,
                    searchText,
                    sortBy,
                },
            })
        );
    }, [dispatch, user.user_id, state, searchText, sortBy]);

    useEffect(() => {
        getAllTdsData();
        getTdsEmpPayrollData();
    }, [getAllTdsData, getTdsEmpPayrollData]);

    useEffect(() => {
        getAllTdsData();
    }, [state.regimeType]);

    useEffect(() => {
        setRegimeType(state.regimeType);
    }, [state.regimeType]);

    useEffect(() => {
        if (tdsStore.tdsAllSuccess && tdsStore.tdsAllData) {
            const api = tdsStore.tdsAllData;

            setTdsAllRecord({
                totalTDSSubmitted: Number(api.summary?.totalTDSSubmitted?.count) || 0,
                totalTDSTrend: api.summary?.totalTDSSubmitted?.trend || "+0% vs last month",
                pendingVerifications: Number(api.summary?.pendingVerifications?.count) || 0,
                approved: Number(api.summary?.approved?.count) || 0,
                approvedRate: api.summary?.approved?.trend || "0% approval rate",
                rejected: Number(api.summary?.rejected?.count) || 0,
                rejectedRate: api.summary?.rejected?.trend || "0% rejection rate",
                avgProcessingTime: api.summary?.avgProcessingTime?.days?.toFixed(1) || "0",
                panPresentPercentage: Number(api.panCompliance?.panPresent?.percentage) || 0,
                panPresentCount: Number(api.panCompliance?.panPresent?.count) || 0,
                panMissingPercentage: Number(api.panCompliance?.panMissing?.percentage) || 0,
                panMissingCount: Number(api.panCompliance?.panMissing?.count) || 0,
                deductionBreakdown: Object.entries(api.deductionBreakdown || {}).map(([key, val]) => ({
                    name: getCapitalizeWord(key.replace(/([A-Z])/g, ' $1').trim()),
                    amount: val.amount,
                    percentage: val.percentage,
                })),
                processingTrends: api.processingTrends || [],
                financialYear: api.metadata?.financialYear || currentFynencialYear(),
                totalEmployees: Number(api.metadata?.totalEmployees) || 0,
            });

            setLoading(false);
            dispatch(initAllTds());
        }

        if (tdsStore.tdsAllError) {
            toast.error(tdsStore.tdsAllError || "Failed to load dashboard data");
            setLoading(false);
        }

        if (tdsStore.tdsEmpListSuccess) {
            setTdsEmpList(tdsStore.tdsEmpListData || []);
            setEmpListLoading(false);
            dispatch(initTdsEmpList());
        }

        if (tdsStore.tdsEmpListError) {
            toast.error(tdsStore.tdsEmpListError || "Failed to load employee list");
            setTdsEmpList([]);
            setEmpListLoading(false);
        }
    }, [tdsStore, dispatch]);

    const columnDefs = [
        {
            headerName: "SR.NO.",
            width: 100,
            pinned: "left",
            valueGetter: "node.rowIndex + 1",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            headerClass: "ag-center-header",
        },
        {
            headerName: "Employee",
            width: "350px",
            pinned: "left",
            cellRenderer: (params) => {
                const employeeName = params.data.employeeName || "N/A";
                const employeeId = params.data.employeeId || "N/A";
                const initials = getInitials(employeeName);
                const bgColor = getInitialColor(employeeName);
                return (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        padding: "12px 0",
                        cursor: "pointer"
                    }}>
                        <div style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: bgColor,
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}>
                            {initials}
                        </div>
                        <div
                            style={{
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "#ffffff",
                                display: "flex",
                                alignItems: "center",
                                gap: "12px"
                            }}
                            className="link-hover"
                            onClick={() => navigateToTaxDeclarations(params.data.staffId)}
                        >
                            <span>{employeeName}</span>
                            <span style={{
                                color: "#a0a0a0",
                                fontWeight: "400",
                                fontSize: "14px"
                            }}>
                                {employeeId}
                            </span>
                        </div>
                    </div>
                );
            },
            flex: 2,
        },
        {
            headerName: "Department",
            width: "180px",
            cellRenderer: (rowData) => <span>{rowData.data.departmentName || "-"}</span>,
        },
        {
            headerName: "Status",
            width: "180px",
            cellStyle: { display: "flex", alignItems: "center", whiteSpace: "normal", lineHeight: "1.2" },
            autoHeight: true,
            cellRenderer: (rowData) => {
                const status = rowData.data.tdsStatus || "pending";
                return <span className={status === "approved" ? "badge-green" : ""}>{getCapitalizeWord(status)}</span>;
            },
        },
        {
            headerName: "Regime",
            width: "180px",
            cellStyle: { display: "flex", alignItems: "center", whiteSpace: "normal", lineHeight: "1.5" },
            autoHeight: true,
            cellRenderer: (rowData) => <span>{rowData.data.regimeType || "-"}</span>,
        },
        {
            headerName: "TDS Amount",
            field: "tdsAmount",
            width: "140px",
            cellRenderer: (rowData) => <span>{rowData.data.tdsAmount || "0"}</span>,
        },
        {
            headerName: "Deductions",
            field: "deductions",
            width: "130px",
            cellRenderer: (rowData) => <span>{rowData.data.deductions || "0"}</span>,
        },
        {
            headerName: "Compliance",
            width: "180px",
            cellStyle: { display: "flex", alignItems: "center", whiteSpace: "normal", lineHeight: "1.2" },
            autoHeight: true,
            cellRenderer: (rowData) => {
                const compliance = rowData.data.tdsComplaintStatus || "non-compliant";
                return <span className={compliance === "compliant" ? "badge-green" : ""}>{getCapitalizeWord(compliance)}</span>;
            },
        },
        {
            headerName: "Action",
            width: "200px",
            pinned: "right",
            cellRenderer: (rowData) => {
                const employeeData = rowData.data;
                return (
                    <div className="d-flex align-items-center gap-3 mt-2">
                        <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={() => navigateToTaxDeclarations(employeeData.staffId)}>
                            <FaEye style={{ fontSize: "18px", color: "#9b51e0" }} />
                        </button>
                        <button className="btn btn-sm btn-outline-success d-flex align-items-center gap-1" onClick={() => downloadEmployeeReport(employeeData)}>
                            <FaDownload style={{ fontSize: "18px", color: "#fff" }} />
                        </button>
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            {/* Filters */}
            <div className="d-flex align-items-center justify-content-between gap-2 mb-4 top-header-db" style={{ marginTop: "-20px" }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-1">
                        <span className="color-white font-weight-200 font-14">Financial Year:</span>
                        <FinancialYear selectedYear={state.financialYear} setSelectedYear={setSelectedYear} />
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <span className="color-white font-weight-200 font-14">Department:</span>
                        <DepartmentList name="departmentId" value={state.departmentId} onChange={handleInputChange} />
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <span className="color-white font-weight-200 font-14">Regime Type:</span>
                        <RegimetList name="regimeType" value={state.regimeType} onChange={handleInputChange} />
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <span className="color-white font-weight-200 font-14">Location:</span>
                        <BranchList name="branchId" value={state.branchId} onChange={handleInputChange} />
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <button style={{ height: "35px" }} className="border-radius-8px d-flex align-items-center gap-2 universal-btn no-wrap-text" onClick={applyFilters}>
                        <Filter /> Apply Filters
                    </button>
                    {(state.financialYear !== currentFynencialYear() || state.departmentId || state.regimeType !== "old" || state.branchId) && (
                        <button style={{ height: "35px" }} className="border-radius-8px d-flex align-items-center gap-2 universal-sec-btn" onClick={clearAllFilter}>
                            <Cross /> Clear
                        </button>
                    )}
                </div>
            </div>

            {/* YOUR EXACT CARD DESIGN FROM THE IMAGE */}
            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-purple" /></div>
            ) : (
                <div className="stats-wrapper mb-4">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <div className="stat-box box-1">
                            <FaFileAlt className="stat-icon text-purple" />
                            <h2 className="font-weight-200 text-purple">{tdsAllRecord.totalTDSSubmitted}</h2>
                            <p className="stat-title">Total 12BB Submitted</p>
                            <p className="stat-sub green-text font-weight-200">
                                ↑ {tdsAllRecord.totalTDSTrend}
                            </p>
                        </div>

                        <div className="stat-box box-2">
                            <FaClock className="stat-icon yellow-text" />
                            <h2 className="font-weight-200 yellow-text">{tdsAllRecord.pendingVerifications}</h2>
                            <p className="stat-title">Pending Verifications</p>
                            <p className="stat-sub text-red">
                                ▲ 23 high priority
                            </p>
                        </div>

                        <div className="stat-box box-3">
                            <FaCheckCircle className="stat-icon green-text" />
                            <h2 className="font-weight-200 green-text">{tdsAllRecord.approved}</h2>
                            <p className="stat-title">Approved</p>
                            <p className="stat-sub green-text">
                                ↑ {tdsAllRecord.approvedRate}
                            </p>
                        </div>

                        <div className="stat-box box-4">
                            <FaTimesCircle className="stat-icon text-red" />
                            <h2 className="font-weight-200 text-red">{tdsAllRecord.rejected}</h2>
                            <p className="stat-title">Rejected</p>
                            <p className="stat-sub text-gray">
                                {tdsAllRecord.rejectedRate}
                            </p>
                        </div>

                        <div className="stat-box box-5">
                            <FaStopwatch className="stat-icon text-info" />
                            <h2 className="font-weight-200 text-info">{tdsAllRecord.avgProcessingTime}</h2>
                            <p className="stat-title">Avg Processing Time</p>
                            <p className="stat-sub blue-text">Days per submission</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Slab-wise + Deduction Breakdown */}
            <Row className="mt-4">
                <Col sm="8">
                    <div className="setting-border height-auto p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h3 className="text-purple font-weight-200 mb-0">Slab-wise Tax Distribution</h3>
                            <div className="d-flex gap-2">
                                <button
                                    className={`border-radius-8px px-4 py-2 ${regimeType === "old" ? "universal-btn" : "universal-sec-btn"}`}
                                    onClick={() => setState(prev => ({ ...prev, regimeType: "old" }))}
                                >
                                    Old Regime
                                </button>
                                <button
                                    className={`border-radius-8px px-4 py-2 ${regimeType === "new" ? "universal-btn" : "universal-sec-btn"}`}
                                    onClick={() => setState(prev => ({ ...prev, regimeType: "new" }))}
                                >
                                    New Regime
                                </button>
                            </div>
                        </div>
                        {tdsAllRecord.deductionBreakdown?.length > 0 ? (
                            <BarChart regimeType={regimeType} data={tdsAllRecord.deductionBreakdown} />
                        ) : (
                            <div className="text-center py-5">
                                <p className="color-gray">No slab-wise data available</p>
                            </div>
                        )}
                    </div>
                </Col>
                <Col sm="4">
                    <div className="setting-border height-auto p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h3 className="text-purple font-weight-200 mb-0">Deduction Breakdown</h3>
                            <CircleInfo class_name="color-gray" />
                        </div>
                        {tdsAllRecord.deductionBreakdown?.length > 0 ? (
                            <DeductionBreakDown data={tdsAllRecord.deductionBreakdown} />
                        ) : (
                            <div className="text-center py-5">
                                <p className="color-gray">Deduction breakdown not available</p>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* PAN Compliance + Processing Time Trends */}
            <Row className="mt-4">
                <Col sm="6">
                    <div className="setting-border height-auto p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h3 className="text-purple font-weight-200 mb-0">Rent ₹1L Compliance</h3>
                            <span className="color-gray font-weight-200">PAN Status</span>
                        </div>
                        <Row className="g-3">
                            <Col sm="6">
                                <div className="p-4 rounded text-center" style={{ background: "rgba(43, 3, 60, 0.1)" }}>
                                    <h2 className="text-green font-weight-200 mb-2" style={{ fontSize: "36px" }}>
                                        {tdsAllRecord.panPresentPercentage}%
                                    </h2>
                                    <p className="color-gray small mb-1" style={{ fontSize: "14px" }}>PAN Present</p>
                                    <p className="text-green" style={{ fontSize: "16px", fontWeight: "500" }}>
                                        {tdsAllRecord.panPresentCount} Employees
                                    </p>
                                </div>
                            </Col>
                            <Col sm="6">
                                <div className="p-4 rounded text-center" style={{ background: "rgba(49, 6, 90, 0.1)" }}>
                                    <h2 className="text-red font-weight-200 mb-2" style={{ fontSize: "36px" }}>
                                        {tdsAllRecord.panMissingPercentage}%
                                    </h2>
                                    <p className="color-gray small mb-1" style={{ fontSize: "14px" }}>PAN Missing</p>
                                    <p className="text-red" style={{ fontSize: "16px", fontWeight: "600" }}>
                                        {tdsAllRecord.panMissingCount} Employees
                                    </p>
                                </div>
                            </Col>
                        </Row>
                        <div className="mt-4">
                            <ProgressBar
                                now={tdsAllRecord.panPresentPercentage}
                                style={{ height: "10px", backgroundColor: "#f0f0f0" }}
                                className="custom-progress"
                            />
                            <div className="d-flex justify-content-between mt-3">
                                <span className="color-gray small font-weight-600">Compliant</span>
                                <span className="color-gray small font-weight-600">Non-Compliant</span>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col sm="6">
                    <div className="setting-border height-auto p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h3 className="text-purple font-weight-200 mb-0">Processing Time Trends</h3>
                            <span className="color-gray font-weight-200">Last 6 months</span>
                        </div>
                        <div
                            style={{
                                height: "360px",           // ← Medium size (was 280px)
                                width: "100%",
                                padding: "10px 0",
                                background: "rgba(30, 30, 50, 0.3)",
                                borderRadius: "12px",
                                margin: "16px 0"
                            }}
                        >
                            <div style={{ height: "100%", width: "100%" }}>
                                {tdsAllRecord.processingTrends.length > 0 ? (
                                    <BarChart
                                        data={tdsAllRecord.processingTrends}
                                    // You can also pass height prop if your BarChart component supports it
                                    />
                                ) : (
                                    <div className="h-100 d-flex align-items-center justify-content-center">
                                        <p className="color-gray">No trend data available yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Export Section */}
            <div className="setting-border height-auto mt-4 mb-4 p-4">
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div>
                        <h3 className="text-purple font-weight-200 mb-1">Export Reports</h3>
                        <h5 className="color-gray font-weight-200">Download comprehensive reports in your preferred format</h5>
                    </div>
                    <div className="d-flex align-items-center gap-2" style={{ position: "relative", top: "10px" }}>
                        <button className="d-flex align-items-center justify-content-center gap-2 mb-4 universal-btn-red border-radius-8px" onClick={() => handleExport("PDF")} disabled={isExporting || tdsEmpList.length === 0}>
                            <FilePdf /> {isExporting ? "Exporting..." : "Export PDF"}
                        </button>
                        <button className="d-flex align-items-center justify-content-center gap-2 mb-4 universal-btn-green border-radius-8px" onClick={() => handleExport("XLSX")} disabled={isExporting || tdsEmpList.length === 0}>
                            <FileExcel /> {isExporting ? "Exporting..." : "Export XLSX"}
                        </button>
                        <button className="d-flex align-items-center justify-content-center gap-2 mb-4 universal-btn-info border-radius-8px" onClick={() => handleExport("CSV")} disabled={isExporting || tdsEmpList.length === 0}>
                            <FileCsv /> {isExporting ? "Exporting..." : "Export CSV"}
                        </button>
                        <button className="d-flex align-items-center justify-content-center gap-2 mb-4 universal-btn border-radius-8px" onClick={() => handleExport("Bulk")} disabled={isExporting || tdsEmpList.length === 0}>
                            <Download /> {isExporting ? "Exporting..." : "Bulk Export"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Employee Table */}
            <Widgets
                icon={<span className="color-white font-weight-200">Employee Details</span>}
                button={
                    <div className="d-flex align-items-center gap-3">
                        <div style={{ position: "relative" }}>
                            <Search class_name={"search-absolute"} />
                            <input
                                placeholder="Search Employees..."
                                className="universal-input padding-left-35-px on-focus-outline-none"
                                style={{ width: "300px" }}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                        <Form.Select className="universal-input on-focus-outline-none" style={{ width: "300px" }} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="name">Sort by name</option>
                            <option value="department">Sort by department</option>
                            <option value="status">Sort by status</option>
                            <option value="tdsAmount">Sort by TDS amount</option>
                        </Form.Select>
                    </div>
                }
                body={
                    <>
                        <div className="ag-theme-quartz custom-grid mt-2" style={{ height: "calc(100vh - 320px)", width: "100%" }}>
                            {empListLoading ? (
                                <TableSkeleton />
                            ) : tdsEmpList.length > 0 ? (
                               <AgGridReact
    rowData={tdsEmpList}
    columnDefs={columnDefs}
    pagination={true}
    paginationPageSize={paginationPageSize}
    paginationPageSizeSelector={paginationPageSizeSelector}
    onGridReady={onGridReady}
    domLayout="normal"
    defaultColDef={{
        resizable: true,
        sortable: true,
        filter: true,
        cellStyle: {
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            fontSize: "14px",
            lineHeight: "1.4",
        },
    }}
    rowHeight={68}
    headerHeight={48}
    suppressHorizontalScroll={false}
    animateRows={true}
    className="ag-theme-quartz"
    onFirstDataRendered={(params) => {
        setTimeout(() => {
            params.api.sizeColumnsToFit();
        }, 100);
    }}
    onGridSizeChanged={(params) => {
        params.api.sizeColumnsToFit();
    }}
/>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="color-gray">No employee records found</p>
                                </div>
                            )}
                        </div>
                        {tdsEmpList.length > 0 && gridApi && (
                            <div className="d-flex align-items-center justify-content-between custom-pagination">
                                <CustomPagination gridApi={gridApi} />
                            </div>
                        )}
                    </>
                }
            />
        </div>
    );
};

export default Dashboard;